// The trends screen's state, as URL search params.
//
// Same reasoning as `regionsSearch.ts`: "the last three years, in USD, for
// Harris" is a link somebody sends before a board meeting, and a screen whose
// controls live only in component state cannot be one. Every field carries its
// own `.catch(undefined)` so one unreadable param falls back alone, and the
// parse runs again at the point of use, because the root route validates
// nothing and a rejected value still reaches the component.
//
// THE WINDOW IS A LENGTH, NOT A PAIR OF MONTHS. The report takes from/to, and
// the screen could expose them — but the question this screen answers is "how
// are we doing", which is always asked of the trailing year or two, ending
// now. A fixed end pinned to today means the link stays true next month: the
// same URL shows the same trailing window, not a window that has quietly
// become history.

import { z } from "zod";
import { Currency, ReportScope, Uuid } from "@crm/shared";
import {
  DEFAULT_TRENDS_QUERY,
  type TrendsQueryState,
  type TrendsWindowMonths,
} from "../../api/queries.js";

const TrendsWindow = z.enum(["12", "24", "36"]);

const TrendsSearchSchema = z.object({
  /**
   * How many trailing months, ending this month. Spelled `window` on the URL
   * rather than `months`: the usage-trend route carries a numeric `months`,
   * and TanStack Router types the search of EVERY route as one intersection,
   * so the same key with two shapes leaves neither screen a type to navigate
   * with.
   */
  window: TrendsWindow.optional().catch(undefined),
  partner_id: Uuid.optional().catch(undefined),
  scope: ReportScope.optional().catch(undefined),
});

export type TrendsSearch = z.infer<typeof TrendsSearchSchema>;

export function validateTrendsSearch(raw: Record<string, unknown>): TrendsSearch {
  const parsed = TrendsSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

export function searchToTrendsState(raw: TrendsSearch, currency: Currency): TrendsQueryState {
  const search = validateTrendsSearch(raw as Record<string, unknown>);
  return {
    months:
      search.window === undefined
        ? DEFAULT_TRENDS_QUERY.months
        : (Number(search.window) as TrendsWindowMonths),
    currency,
    partnerId: search.partner_id ?? DEFAULT_TRENDS_QUERY.partnerId,
    scope: search.scope ?? DEFAULT_TRENDS_QUERY.scope,
  };
}

/** Defaults omitted, so the trailing year is a clean `/trends`. The currency is never written. */
export function trendsStateToSearch(state: TrendsQueryState): TrendsSearch {
  return {
    ...(state.months === DEFAULT_TRENDS_QUERY.months
      ? {}
      : { window: String(state.months) as "24" | "36" }),
    ...(state.partnerId === null ? {} : { partner_id: state.partnerId }),
    // The scope only means something alongside a partner.
    ...(state.partnerId === null || state.scope === DEFAULT_TRENDS_QUERY.scope
      ? {}
      : { scope: state.scope }),
  };
}
